import React, { useContext, useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	IconButton,
	InputAdornment,
	InputLabel,
	OutlinedInput,
	Paper,
	Snackbar,
	Stack,
	Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { UserDto, UserSessionDto } from "../../models/User";
import { AppContext } from "../../Contexts/AppContext";
import UserApi from "../../api/user";
import {
	getUserPermissionariesIds,
	getUserRoleForPermissionary,
} from "../../utils/userPermissionaries";

export default function LoginScreen(): JSX.Element {
	document.title = "Inicio de sesion";
	const navigate = useNavigate();
	const [user, setUser] = useState<UserDto>({
		email: "",
		password: "",
	});
	const [loading, setLoading] = useState<boolean>(false);
	const [open, setOpen] = useState<boolean>(false);
	const [notificationMessage, setNotificationMessage] = useState<string>("");
	const [severity, setSeverity] = useState<"success" | "error">("success");
	const [showPassword, setShowPassword] = useState<boolean>(false);

	const { setTitle, setUserContext, userContext } = useContext(AppContext);

	const handleClose = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}

		setOpen(false);
	};

	const loginAction = async () => {
		setLoading(true);
		try {
			const session = (await UserApi.login(user)) as UserSessionDto;
			const { accessToken, userId, permissionaryName, permissionaryRoles } =
				session;
			const permissionariesIds = getUserPermissionariesIds(session);
			const selectedPermissionaryId = permissionariesIds[0] || "";
			const selectedRole = getUserRoleForPermissionary(
				session,
				selectedPermissionaryId
			);
			if (!accessToken) {
				throw new Error("Authentication Error");
			}
			localStorage.setItem("accessToken", accessToken);
			setUserContext({
				accessToken,
				userId,
				role: selectedRole,
				permissionariesIds,
				permissionaryRoles,
				permissionaryId: selectedPermissionaryId,
				permissionaryName,
			});
			navigate("/devices");
		} catch (exception: any) {
			console.error(exception);
			setNotificationMessage("Error de autenticacion");
			setSeverity("error");
			setOpen(true);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		setTitle("Inicio de sesion");
		if (userContext) {
			navigate("/devices");
		}
	}, [navigate, setTitle, userContext]);

	return (
		<Box
			sx={{
				minHeight: "100vh",
				background:
					"radial-gradient(circle at top left, rgba(20, 184, 166, 0.18), transparent 28%), linear-gradient(135deg, #07131e 0%, #0f2233 48%, #eaf2f6 48%, #f7f9fb 100%)",
				px: 2,
				py: 3,
			}}>
			<Stack
				direction='row'
				alignItems='center'
				justifyContent='space-between'
				sx={{ maxWidth: 1200, mx: "auto", mb: 5 }}>
				<Stack direction='row' spacing={1.5} alignItems='center'>
					<Box
						sx={{
							width: 42,
							height: 42,
							borderRadius: "14px",
							display: "grid",
							placeItems: "center",
							background:
								"linear-gradient(135deg, #38bdf8 0%, #14b8a6 100%)",
							color: "#04111d",
						}}>
						<BoltRoundedIcon />
					</Box>
					<Box>
						<Typography
							sx={{
								fontSize: 12,
								letterSpacing: "0.24em",
								textTransform: "uppercase",
								color: "rgba(226, 232, 240, 0.72)",
							}}>
							Permissionaries
						</Typography>
						<Typography sx={{ fontWeight: 700, color: "#f8fafc" }}>
							Acceso para suministradores calificados
						</Typography>
					</Box>
				</Stack>

				<Button
					startIcon={<ArrowBackRoundedIcon />}
					onClick={() => navigate("/")}
					sx={{
						textTransform: "none",
						fontWeight: 700,
						color: "#f8fafc",
					}}>
					Volver
				</Button>
			</Stack>

			<Box
				sx={{
					maxWidth: 1200,
					mx: "auto",
					display: "grid",
					gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
					gap: 4,
					alignItems: "center",
				}}>
				<Box sx={{ color: { xs: "#f8fafc", md: "#0f172a" }, pr: { md: 4 } }}>
					<Typography
						sx={{
							fontSize: { xs: "2.4rem", md: "4rem" },
							fontWeight: 800,
							letterSpacing: "-0.05em",
							lineHeight: 0.98,
							maxWidth: 560,
							mb: 2,
							color: "#f8fafc",
							textShadow: "0 10px 30px rgba(2, 6, 23, 0.36)",
						}}>
						Ingresa para supervisar tu operacion de medicion.
					</Typography>
					<Typography
						sx={{
							fontSize: "1.05rem",
							lineHeight: 1.8,
							maxWidth: 560,
							color: "#dbe7f3",
							textShadow: "0 6px 24px rgba(2, 6, 23, 0.24)",
						}}>
						Consulta dispositivos, usuarios, zonas, eventos y configuraciones
						desde un acceso unificado para tu permisionario.
					</Typography>
				</Box>

				<Paper
					elevation={0}
					sx={{
						width: "100%",
						maxWidth: 480,
						justifySelf: "center",
						p: { xs: 3, md: 4 },
						borderRadius: "28px",
						backgroundColor: "rgba(255, 255, 255, 0.92)",
						border: "1px solid rgba(148, 163, 184, 0.18)",
						boxShadow: "0 24px 50px rgba(15, 23, 42, 0.16)",
					}}>
					<Stack spacing={3}>
						<Box>
							<Typography
								sx={{
									fontSize: "1.85rem",
									fontWeight: 800,
									color: "#0f172a",
									mb: 1,
								}}>
								Inicio de sesion
							</Typography>
							<Typography sx={{ color: "#64748b", lineHeight: 1.7 }}>
								Accede con tu cuenta autorizada para entrar al panel de
								monitoreo.
							</Typography>
						</Box>

						<FormControl variant='outlined' fullWidth>
							<InputLabel htmlFor='outlined-adornment-username'>Email</InputLabel>
							<OutlinedInput
								id='outlined-adornment-username'
								type='text'
								label='Email'
								value={user.email}
								onChange={(event) => {
									setUser({ ...user, email: event.target.value.toLowerCase() });
								}}
								onKeyUp={(event) => {
									if (event.key === "Enter") {
										loginAction();
									}
								}}
							/>
						</FormControl>

						<FormControl variant='outlined' fullWidth>
							<InputLabel htmlFor='outlined-adornment-password'>
								Contraseña
							</InputLabel>
							<OutlinedInput
								id='outlined-adornment-password'
								type={showPassword ? "text" : "password"}
								label='Contraseña'
								value={user.password}
								endAdornment={
									<InputAdornment position='end'>
										<IconButton
											aria-label='toggle password visibility'
											onClick={() => setShowPassword(!showPassword)}
											edge='end'>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								}
								onChange={(event) => {
									setUser({ ...user, password: event.target.value });
								}}
								onKeyUp={(event) => {
									if (event.key === "Enter") {
										loginAction();
									}
								}}
							/>
						</FormControl>

						{loading ? (
							<Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
								<CircularProgress size={30} />
							</Box>
						) : (
							<Button
								variant='contained'
								fullWidth
								onClick={loginAction}
								sx={{
									py: 1.5,
									borderRadius: "16px",
									textTransform: "none",
									fontWeight: 700,
									background:
										"linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%)",
								}}>
								Ingresar
							</Button>
						)}
					</Stack>
				</Paper>
			</Box>

			<Snackbar
				open={open}
				autoHideDuration={6000}
				onClose={handleClose}
				message={notificationMessage}>
				<Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
					{notificationMessage}
				</Alert>
			</Snackbar>
		</Box>
	);
}
